/**
 * One task row: grip, animated checkbox, inline-editable title, due chip,
 * global number badge, expand chevron. Completion motion: checkbox spring +
 * strikethrough sweep here, the sink itself is the parent's FLIP pass.
 */
import { useRef, useState } from "react";
import * as DropdownMenu from "@radix-ui/react-dropdown-menu";
import { Check, ChevronDown, GripVertical, MoreHorizontal, Pencil, Repeat, Trash2, Undo2 } from "lucide-react";
import type { TaskItem } from "@hydra-notes/shared";
import { describeRecurrence, dueLabel } from "@hydra-notes/shared";
import { IconButton } from "@/ui/IconButton";
import s from "./TaskListView.module.css";

export type DropPos = "above" | "below";

export function TaskRow({
  task,
  readOnly,
  expanded,
  sunk,
  dropPos,
  flipRef,
  onToggleDone,
  onToggleExpand,
  onRename,
  onDelete,
  onDragStart,
  onDragOver,
  onDrop,
  onDragEnd,
}: {
  task: TaskItem;
  readOnly: boolean;
  expanded: boolean;
  /** In the session-done pile: grayed, struck, not draggable. */
  sunk: boolean;
  dropPos: DropPos | null;
  flipRef: (el: HTMLElement | null) => void;
  onToggleDone: (el: Element | null) => void;
  onToggleExpand: () => void;
  onRename: (title: string) => void;
  onDelete: () => void;
  onDragStart: (e: React.DragEvent) => void;
  onDragOver: (e: React.DragEvent) => void;
  onDrop: (e: React.DragEvent) => void;
  onDragEnd: () => void;
}) {
  const checkRef = useRef<HTMLButtonElement>(null);
  const [editing, setEditing] = useState(false);

  const today = new Date();
  const todayIso = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, "0")}-${String(
    today.getDate(),
  ).padStart(2, "0")}`;
  const dueTone = !task.dueDate || task.done ? "" : task.dueDate < todayIso ? s.dueOverdue : task.dueDate === todayIso ? s.dueToday : "";

  return (
    <div
      ref={flipRef}
      className={[
        s.row,
        sunk ? s.rowSunk : "",
        expanded ? s.rowExpanded : "",
        dropPos === "above" ? s.dropAbove : dropPos === "below" ? s.dropBelow : "",
      ].join(" ")}
      draggable={!readOnly && !sunk && !editing}
      onDragStart={onDragStart}
      onDragOver={onDragOver}
      onDrop={onDrop}
      onDragEnd={onDragEnd}
      data-task-id={task.id}
    >
      <span className={s.grip} aria-hidden>
        <GripVertical size={13} />
      </span>
      <button
        ref={checkRef}
        type="button"
        className={[s.check, task.done ? s.checkDone : ""].join(" ")}
        role="checkbox"
        aria-checked={task.done}
        aria-label={task.done ? `Mark "${task.title}" not done` : `Mark "${task.title}" done`}
        disabled={readOnly}
        onClick={() => onToggleDone(checkRef.current)}
      >
        <Check size={12} strokeWidth={3} className={s.checkIcon} />
      </button>

      {editing ? (
        <input
          className={s.titleEdit}
          defaultValue={task.title}
          autoFocus
          onFocus={(e) => e.target.select()}
          onBlur={(e) => {
            setEditing(false);
            const v = e.target.value.trim();
            if (v && v !== task.title) onRename(v);
          }}
          onKeyDown={(e) => {
            if (e.key === "Enter") (e.target as HTMLInputElement).blur();
            if (e.key === "Escape") {
              (e.target as HTMLInputElement).value = task.title;
              (e.target as HTMLInputElement).blur();
            }
          }}
        />
      ) : (
        <span
          className={[s.title, task.done ? s.titleDone : ""].join(" ")}
          onClick={() => {
            if (!readOnly && !sunk) setEditing(true);
            else onToggleExpand();
          }}
        >
          {task.title || "Untitled task"}
        </span>
      )}

      {task.recurrence ? (
        <span className={s.recurBadge} title={`Repeats ${describeRecurrence(task.recurrence)}`}>
          <Repeat size={12} />
        </span>
      ) : null}
      {task.dueDate ? (
        <span className={[s.due, dueTone].join(" ")}>{dueLabel(task.dueDate, task.dueTime)}</span>
      ) : null}
      {task.description ? <span className={s.hasNote} title="Has details" /> : null}
      <span className={s.seq}>{task.seq !== undefined ? `#${task.seq}` : "#···"}</span>
      <DropdownMenu.Root>
        <DropdownMenu.Trigger asChild>
          <span className={s.taskMenuTrigger}>
            <IconButton label="Task actions"><MoreHorizontal size={13} /></IconButton>
          </span>
        </DropdownMenu.Trigger>
        <DropdownMenu.Portal>
          <DropdownMenu.Content className="hn-menu" align="end" sideOffset={5}>
            <DropdownMenu.Item className="hn-menu-item" onSelect={onToggleExpand}>
              <ChevronDown /> {expanded ? "Hide details" : "Show details"}
            </DropdownMenu.Item>
            {!readOnly && !sunk ? (
              <DropdownMenu.Item className="hn-menu-item" onSelect={() => setEditing(true)}>
                <Pencil /> Rename
              </DropdownMenu.Item>
            ) : null}
            {!readOnly ? (
              <DropdownMenu.Item className="hn-menu-item" onSelect={() => onToggleDone(null)}>
                {task.done ? <Undo2 /> : <Check />} {task.done ? "Mark not done" : "Mark complete"}
              </DropdownMenu.Item>
            ) : null}
            {!readOnly ? (
              <>
                <DropdownMenu.Separator className="hn-menu-sep" />
                <DropdownMenu.Item
                  className="hn-menu-item danger"
                  onSelect={() => {
                    if (confirm(`Delete "${task.title || "Untitled task"}" forever? This cannot be undone.`)) onDelete();
                  }}
                >
                  <Trash2 /> Delete task
                </DropdownMenu.Item>
              </>
            ) : null}
          </DropdownMenu.Content>
        </DropdownMenu.Portal>
      </DropdownMenu.Root>
      <button
        type="button"
        className={[s.expand, expanded ? s.expandOpen : ""].join(" ")}
        aria-label={expanded ? "Collapse details" : "Show details"}
        onClick={onToggleExpand}
      >
        <ChevronDown size={13} />
      </button>
    </div>
  );
}
